import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import './ForgemarkSignIn.css';

const friendlyError = (code) => {
  switch (code) {
    case 'auth/invalid-email': return 'That email address does not look right.';
    case 'auth/missing-password': return 'Enter your password.';
    case 'auth/weak-password': return 'Password should be at least 6 characters.';
    case 'auth/email-already-in-use': return 'An account already exists for that email. Try signing in.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found': return 'Email or password is incorrect.';
    case 'auth/too-many-requests': return 'Too many attempts. Give it a minute, then try again.';
    case 'auth/popup-closed-by-user': return 'Google sign-in was cancelled.';
    default: return 'Something went wrong. Please try again.';
  }
};

export default function ForgemarkSignIn() {
  const [mode, setMode] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const { user, loading, signIn, signUp, signInWithGoogle } = useAuth();

  // Already signed in (e.g. returning visitor)? Skip the gate.
  useEffect(() => {
    if (!loading && user) navigate('/games', { replace: true });
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (mode === 'register') {
        await signUp(email, password, username);
      } else {
        await signIn(email, password);
      }
      navigate('/games', { replace: true });
    } catch (err) {
      setError(friendlyError(err && err.code));
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    setBusy(true);
    try {
      await signInWithGoogle();
      navigate('/games', { replace: true });
    } catch (err) {
      setError(friendlyError(err && err.code));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fm-root">
      {/* Animated background — forge sparks + deep red glow */}
      <div className="fm-bg" aria-hidden="true">
        {[...Array(18)].map((_, i) => (
          <div key={i} className={`fm-spark fm-spark-${i}`} />
        ))}
        <div className="fm-forge-glow" />
      </div>

      <div className="fm-center">

        {/* Logo image IS the brand header */}
        <div className="fm-brand">
          <img
            src="/forgemark-logo.png"
            alt="Forgemark"
            className="fm-logo-img"
          />
        </div>

        {/* Ornament divider */}
        <div className="fm-ornament" aria-hidden="true">
          <span className="fm-ornament-line"></span>
          <span className="fm-ornament-gem">✦</span>
          <span className="fm-ornament-line"></span>
        </div>

        {/* Tab toggle */}
        <div className="fm-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={mode === 'signin'}
            className={`fm-tab ${mode === 'signin' ? 'fm-tab-active' : ''}`}
            onClick={() => setMode('signin')}
          >
            Sign In
          </button>
          <button
            role="tab"
            aria-selected={mode === 'register'}
            className={`fm-tab ${mode === 'register' ? 'fm-tab-active' : ''}`}
            onClick={() => setMode('register')}
          >
            Register
          </button>
        </div>

        {/* Form panel */}
        <div className="fm-panel">
          <button
            type="button"
            className="fm-btn-google"
            onClick={handleGoogle}
            disabled={busy}
          >
            <svg className="fm-google-icon" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.85.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"/>
              <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"/>
            </svg>
            Continue with Google
          </button>

          <div className="fm-divider" aria-hidden="true"><span>or</span></div>

          {error && <div className="fm-error" role="alert">{error}</div>}

          <form onSubmit={handleSubmit} className="fm-form" noValidate>
            {mode === 'register' && (
              <div className="fm-field">
                <label className="fm-label" htmlFor="username">Adventurer Name</label>
                <input
                  id="username"
                  className="fm-input"
                  type="text"
                  placeholder="Choose your name, traveler"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  autoComplete="username"
                />
              </div>
            )}

            <div className="fm-field">
              <label className="fm-label" htmlFor="email">Email</label>
              <input
                id="email"
                className="fm-input"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>

            <div className="fm-field">
              <label className="fm-label" htmlFor="password">Password</label>
              <input
                id="password"
                className="fm-input"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
              />
            </div>

            <button type="submit" className="fm-btn-primary" disabled={busy}>
              {busy
                ? 'One moment…'
                : (mode === 'signin' ? 'Enter the Realm' : 'Begin Your Journey')}
            </button>

            {mode === 'signin' && (
              <p className="fm-forgot">
                <a href="#forgot" onClick={e => e.preventDefault()}>Forgot your password?</a>
              </p>
            )}
          </form>
        </div>

        <p className="fm-switch">
          {mode === 'signin'
            ? <>No account? <button className="fm-link-btn" onClick={() => setMode('register')}>Create one</button></>
            : <>Already registered? <button className="fm-link-btn" onClick={() => setMode('signin')}>Sign in</button></>
          }
        </p>

        <p className="fm-footer">© 2026 Forgemark · All rights reserved</p>
      </div>
    </div>
  );
}